<template>
  <div class="linkTypePicker linkTypePicker--panel" aria-label="选择资源类型">
    <div class="linkTypePicker__searchWrap">
      <i class="el-icon-search linkTypePicker__searchIcon" aria-hidden="true" />
      <input
        v-model.trim="searchQuery"
        type="search"
        class="linkTypePicker__searchInput"
        placeholder="搜索资源类型"
        aria-label="搜索资源类型"
      />
    </div>

    <section v-if="recentOptions.length && !searchQuery" class="linkTypePicker__section">
      <h3 class="linkTypePicker__sectionTitle">最近使用</h3>
      <div class="linkTypePicker__grid linkTypePicker__grid--recent">
        <button
          v-for="item in recentOptions"
          :key="'recent-' + item.assetType"
          type="button"
          class="linkTypePicker__cell"
          :class="{ 'linkTypePicker__cell--selected': item.assetType === selectedAssetType }"
          @click="onCellClick(item.assetType)"
        >
          <span class="linkTypePicker__chip">
            <span class="linkTypePicker__chipLabel">{{ item.assetType }}</span>
          </span>
        </button>
      </div>
    </section>

    <section class="linkTypePicker__section">
      <h3 class="linkTypePicker__sectionTitle">
        {{ searchQuery ? '搜索结果' : '全部资源' }}
        <span class="linkTypePicker__sectionCount">{{ filteredOptions.length }}</span>
      </h3>
      <div
        ref="grid"
        class="linkTypePicker__grid linkTypePicker__grid--all"
      >
        <span
          v-if="selectedAssetType && !searchQuery"
          class="linkTypePicker__slideIndicator"
          :class="{ 'linkTypePicker__slideIndicator--ready': indicatorReady }"
          :style="indicatorStyle"
          aria-hidden="true"
        />
        <button
          v-for="item in filteredOptions"
          :key="item.assetType"
          ref="cell"
          type="button"
          class="linkTypePicker__cell"
          :class="{ 'linkTypePicker__cell--selected': item.assetType === selectedAssetType }"
          @click="onCellClick(item.assetType)"
        >
          <span class="linkTypePicker__chip">
            <span class="linkTypePicker__chipLabel">{{ item.assetType }}</span>
          </span>
        </button>
        <p v-if="!filteredOptions.length" class="linkTypePicker__empty">
          没有匹配的类型
        </p>
      </div>
    </section>
  </div>
</template>

<script>
import { flatTypeOptions } from './genericResourceTypePickerMeta'
import { recentAssetTypes } from './genericResourceTypeRecent'

export default {
  name: 'GenericResourceTypePicker',
  props: {
    typeOptions: { type: Array, default: () => [] },
    selectedAssetType: { type: String, default: '' },
  },
  data() {
    return {
      searchQuery: '',
      indicatorStyle: { opacity: 0 },
      indicatorReady: false,
      gridResizeObserver: null,
    }
  },
  computed: {
    flatOptions() {
      // 全部资源保持登记顺序。使用频次只排上面的「最近使用」，避免两块一起挪。
      return flatTypeOptions(this.typeOptions)
    },
    recentOptions() {
      const recent = recentAssetTypes(this.typeOptions, 6)
      return recent.map((assetType) => ({ assetType }))
    },
    filteredOptions() {
      const q = String(this.searchQuery || '').trim().toLowerCase()
      if (!q) {
        return this.flatOptions
      }
      return this.flatOptions.filter((item) =>
        item.assetType.toLowerCase().includes(q)
      )
    },
  },
  watch: {
    selectedAssetType() {
      this.$nextTick(this.syncSlideIndicator)
    },
    filteredOptions() {
      this.$nextTick(this.syncSlideIndicator)
    },
    searchQuery() {
      this.indicatorReady = false
    },
  },
  mounted() {
    this.bindEmbeddedMeasure()
  },
  updated() {
    if (this.selectedAssetType && !this.indicatorReady && !this.searchQuery) {
      this.syncSlideIndicator()
    }
  },
  beforeDestroy() {
    this.unbindEmbeddedMeasure()
  },
  methods: {
    onCellClick(assetType) {
      this.syncSlideIndicatorTo(assetType)
      this.$emit('select', assetType)
    },
    syncSlideIndicator() {
      if (!this.selectedAssetType || this.searchQuery) {
        return
      }
      this.syncSlideIndicatorTo(this.selectedAssetType)
    },
    syncSlideIndicatorTo(assetType) {
      const grid = this.$refs.grid
      const cells = this.$refs.cell
      if (!grid || !cells) {
        return
      }
      const index = this.filteredOptions.findIndex((item) => item.assetType === assetType)
      if (index < 0) {
        this.indicatorReady = false
        return
      }
      const cell = Array.isArray(cells) ? cells[index] : cells
      if (!cell) {
        return
      }
      const gridRect = grid.getBoundingClientRect()
      const cellRect = cell.getBoundingClientRect()
      if (!gridRect.width || !cellRect.width) {
        return
      }
      this.indicatorStyle = {
        opacity: 1,
        width: `${cellRect.width}px`,
        height: `${cellRect.height}px`,
        transform: `translate(${Math.round(cellRect.left - gridRect.left)}px, ${Math.round(cellRect.top - gridRect.top)}px)`,
      }
      this.indicatorReady = true
    },
    bindEmbeddedMeasure() {
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          this.syncSlideIndicator()
          const grid = this.$refs.grid
          if (!grid || typeof ResizeObserver === 'undefined') {
            return
          }
          this.unbindEmbeddedMeasure()
          this.gridResizeObserver = new ResizeObserver(() => {
            this.syncSlideIndicator()
          })
          this.gridResizeObserver.observe(grid)
        })
      })
    },
    unbindEmbeddedMeasure() {
      if (this.gridResizeObserver) {
        this.gridResizeObserver.disconnect()
        this.gridResizeObserver = null
      }
    },
  },
}
</script>

<style scoped>
.linkTypePicker--panel {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px;
  max-height: min(360px, 48vh);
  overflow: auto;
}

.linkTypePicker__searchWrap {
  position: relative;
}

.linkTypePicker__searchIcon {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 14px;
  color: var(--link-ink-muted, #78716c);
  pointer-events: none;
}

.linkTypePicker__searchInput {
  width: 100%;
  height: 34px;
  padding: 0 12px 0 32px;
  border: 1px solid var(--link-line-strong, #e7e5e4);
  border-radius: 8px;
  font-size: 13px;
  color: var(--link-ink, #2c2825);
  background: var(--link-card-surface, #fff);
  box-sizing: border-box;
}

.linkTypePicker__searchInput:focus {
  outline: 2px solid color-mix(in srgb, var(--link-accent, #a67c52) 35%, transparent);
  border-color: var(--link-accent, #a67c52);
}

.linkTypePicker__sectionTitle {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0 0 8px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--link-ink-muted, #78716c);
}

.linkTypePicker__sectionCount {
  font-weight: 500;
  color: var(--link-ink-secondary, #57534e);
}

.linkTypePicker__grid {
  position: relative;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0;
  width: 100%;
}

.linkTypePicker__grid--recent {
  margin-bottom: 4px;
}

.linkTypePicker__slideIndicator {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 0;
  border-radius: 18px 20px 16px 19px;
  background: var(--link-accent-soft, #faf3eb);
  border: 1px solid var(--link-accent, #c9956e);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.92),
    0 2px 8px rgba(166, 124, 82, 0.14);
  opacity: 0;
  pointer-events: none;
  transition:
    transform 0.36s cubic-bezier(0.34, 1.15, 0.64, 1),
    width 0.36s cubic-bezier(0.34, 1.15, 0.64, 1),
    height 0.36s cubic-bezier(0.34, 1.15, 0.64, 1),
    opacity 0.18s ease;
}

.linkTypePicker__slideIndicator--ready {
  opacity: 1;
}

.linkTypePicker__cell {
  position: relative;
  z-index: 1;
  display: block;
  width: 100%;
  padding: 0;
  margin: 0;
  border: none;
  background: transparent;
  cursor: pointer;
}

.linkTypePicker__chip {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 40px;
  padding: 8px 10px;
  box-sizing: border-box;
  border: 1px solid var(--link-line-strong, #e5dfd6);
  border-radius: 18px 20px 16px 19px;
  margin: 0 -1px -1px 0;
  background: var(--link-card-surface, #fff);
  transition:
    background-color 0.2s ease,
    border-color 0.2s ease;
}

.linkTypePicker__chipLabel {
  font-size: 12px;
  font-weight: 500;
  color: var(--link-ink, #2c2825);
  text-align: center;
  word-break: break-word;
}

.linkTypePicker__cell--selected .linkTypePicker__chip {
  border-color: transparent;
  background: transparent;
}

.linkTypePicker__cell--selected .linkTypePicker__chipLabel {
  color: var(--link-accent, #a67c52);
  font-weight: 600;
}

.linkTypePicker__empty {
  grid-column: 1 / -1;
  margin: 8px 0;
  font-size: 12px;
  color: var(--link-ink-muted, #78716c);
  text-align: center;
}

@media (hover: hover) and (pointer: fine) {
  .linkTypePicker__cell:hover .linkTypePicker__chip,
  .linkTypePicker__cell:focus-visible .linkTypePicker__chip {
    border-color: color-mix(in srgb, var(--link-accent, #a67c52) 40%, var(--link-line-strong, #e5dfd6));
    background: var(--link-surface-muted, #f7f4ef);
  }

  .linkTypePicker__cell--selected:hover .linkTypePicker__chip {
    border-color: transparent;
    background: transparent;
  }
}
</style>
